
import React from "react";

import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { TrendingUp, Users, Calendar, UserPlus, Building } from "lucide-react";

interface DashboardStatsProps {
  language: string;
  className?: string;
}

const DashboardStats: React.FC<DashboardStatsProps> = ({ language, className }) => {
  // Fetch real stats from database
  const { data: stats = [] } = useQuery({
    queryKey: ["dashboardStats"],
    queryFn: async () => {
      // Get total events count
      const { count: eventsCount } = await supabase
        .from("scraped_events")
        .select("*", { count: "exact", head: true })
        .eq("is_published", true);

      // Get visitors count
      const { count: visitorsCount } = await supabase
        .from("visitors")
        .select("*", { count: "exact", head: true });

      // Get registrations count
      const { count: registrationsCount } = await supabase
        .from("space_registrations")
        .select("*", { count: "exact", head: true });

      // Get scrape sources count (as partners)
      const { count: sourcesCount } = await supabase
        .from("scrape_sources")
        .select("*", { count: "exact", head: true })
        .eq("is_active", true);

      return [
        {
          title: { en: "Total Events", ar: "إجمالي الفعاليات" },
          value: eventsCount?.toString() || "0",
          icon: Calendar,
        },
        {
          title: { en: "Active Users", ar: "المستخدمين النشطين" },
          value: visitorsCount?.toString() || "0",
          icon: Users,
        },
        {
          title: { en: "Registrations", ar: "التسجيلات" },
          value: registrationsCount?.toString() || "0",
          icon: UserPlus,
        },
        {
          title: { en: "Active Sources", ar: "المصادر النشطة" },
          value: sourcesCount?.toString() || "0",
          icon: Building,
        },
      ];
    },
  });

  return (
    <div className={`grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 w-full ${className}`}>
      {stats.map((stat, idx) => (
        <div 
          key={idx} 
          className="overflow-hidden transition-all duration-300 hover:shadow-lg bg-white/[0.08] border border-white/[0.12] rounded-lg w-full min-w-0"
        >
          <div className="pb-2 flex flex-row justify-between items-start gap-2 p-3 md:p-4">
            <span className="text-xs md:text-sm font-medium text-white/60 min-w-0 truncate">
              {stat.title[language as keyof typeof stat.title]}
            </span>
            <div className="rounded-full bg-primary/20 p-1.5 md:p-2 shrink-0">
              <stat.icon className="h-4 w-4 md:h-5 md:w-5 text-primary-foreground" />
            </div>
          </div>
          <div className="p-3 md:p-4 pt-0 md:pt-0">
            <div className="text-xl md:text-2xl font-bold text-white">{stat.value}</div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default DashboardStats;
